<template>
  <v-skeleton-loader
    :loading="isLoading"
    transition="expand-transition"
    type="article"
  >
    <FormProcessCheckSheet
      :ProcessCheckSheet="editProcessCheckSheet"
      :stockItem="stockItem"
      :isLoading="isSaving"
      :isUndefined="true"
      @save="save"
      @update="update"
      @cancel="$router.push({name: from.name })" />
  </v-skeleton-loader>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import loading from '@codehq/aurora-app-core/src/mixins/loading';
import FormProcessCheckSheet from '../components/FormProcessCheckSheet.vue';
import stockItem from './stockItem';

export default {
  components: {
    FormProcessCheckSheet,
  },
  name: 'EditProcessCheckSheet',
  module: 'ProcessCheckSheet',
  mixins: [loading, stockItem],
  data() {
    return {
      /**
      * Get/Set editing ProcessCheckSheet. Default to {}.
      */
      editProcessCheckSheet: {
        saltBins: [{}],
        drainedWeights: [{}],
      },
      /**
       * Query Id for ProcessCheckSheet
       */
      processCheckSheetId: this.$route?.query.id,
    };
  },
  computed: {
    /** Vuex states
     */
    ...mapState('processCheckSheets', ['processCheckSheets', 'processCheckSheet']),
    /** Get the mode for this view, create or update
     */
    mode() {
      if (this.editProcessCheckSheet.id) {
        return 'edit';
      }
      return 'add';
    },
  },
  destroyed() {
    this.$off('stockItem.loaded');
  },
  async created() {
    this.$on('stockItem.loaded', async (item) => {
      this.isLoading = true;
      if (item.processCheckSheets?.length > 0) {
        // eslint-disable-next-line prefer-destructuring
        this.editProcessCheckSheet = item.processCheckSheets[0];
      } else {
        this.editProcessCheckSheet.stockItemId = item.id;
        this.editProcessCheckSheet.deliveryDate = this.formatDate(item.deliveryNote.receivingDate);
        this.editProcessCheckSheet.supplier = item.deliveryNote.farm.name;
        this.editProcessCheckSheet.farmGradeRange = `${item.sizeMin} - ${item.sizeMax}`;
        this.editProcessCheckSheet.supplierComments = item.deliveryNote.supplierNotes;
        this.editProcessCheckSheet.deliveryWeight = item.receivedMass;
        this.editProcessCheckSheet.processingDatePouches = this.formatDate(item.productionDate);
        this.editProcessCheckSheet.processingDateCans = this.formatDate(item.productionDate);
        this.$log.info('Prepopulation completed');
        this.$forceUpdate();
      }
      this.isLoading = false;
    });
  },
  methods: {
    /** Vuex methods
     */
    ...mapActions('processCheckSheets', ['LOAD_processCheckSheet', 'CREATE_processCheckSheet', 'UPDATE_processCheckSheet', 'LOAD_processCheckSheetByStockItem']),
    ...mapActions('saltBins', ['CREATE_saltBin', 'UPDATE_saltBin']),
    ...mapActions('drainedWeights', ['CREATE_drainedWeight', 'UPDATE_drainedWeight']),
    ...mapActions('stockItems', ['SPLIT_stockItems']),
    async update(item) {
      await this.save(item, true);
    },
    /** Method used to save the item
     */
    async save(processCheckSheet, skipRedirect) {
      this.isSaving = true;
      if (this.mode === 'add') {
        await this.CREATE_processCheckSheet(processCheckSheet);
      } else {
        const updatedProcessCheckSheet = { ...processCheckSheet };
        const { saltBins, drainedWeights } = processCheckSheet;
        updatedProcessCheckSheet.saltBins = undefined;
        updatedProcessCheckSheet.drainedWeights = undefined;
        await this.UPDATE_processCheckSheet(updatedProcessCheckSheet);
        saltBins.forEach((b) => {
          if (b.id) {
            this.UPDATE_saltBin(b);
          } else {
            const saltBin = { ...b };
            saltBin.processCheckSheetId = this.editProcessCheckSheet.id;
            this.CREATE_saltBin(saltBin);
          }
        });
        drainedWeights.forEach((b) => {
          if (b.id) {
            this.UPDATE_drainedWeight(b);
          } else {
            const drainedWeight = { ...b };
            drainedWeight.processCheckSheetId = this.editProcessCheckSheet.id;
            this.CREATE_drainedWeight(drainedWeight);
          }
        });
      }
      const wet = parseFloat(processCheckSheet.totalOutOfSaltWeightWet ?? '0');
      const dried = parseFloat(processCheckSheet.totalOutOfSaltWeightDry ?? '0');
      const totalSaltBinCombo = processCheckSheet.saltBins.reduce(
        (total, obj) => total + parseFloat(obj.saltBinCombo ?? '0'),
        0,
      );
      console.debug({ totalSaltBinCombo });
      const totalWetMeatAndBloodWeightPerc = totalSaltBinCombo / this.stockItem.receivedMass;
      const total = wet + dried;
      const newItems = [];
      // eslint-disable-next-line max-len
      const totalWetMeatAndBloodWeight = this.stockItem.receivedMass * totalWetMeatAndBloodWeightPerc;
      console.debug(totalWetMeatAndBloodWeight, totalWetMeatAndBloodWeightPerc);
      if (wet > 0) {
        const wetPercentage = (wet / total) * 100;
        const item = {
          stockType: 'Wet',
          sizeMin: this.stockItem.sizeMin,
          sizeMax: this.stockItem.sizeMax,
          deliveryNoteId: this.stockItem.deliveryNoteId,
          receivedMass: this.stockItem.receivedMass * (wetPercentage / 100),
          packedMass: this.stockItem.packedMass * (wetPercentage / 100),
          harvestedMass: this.stockItem.harvestedMass * (wetPercentage / 100),
          processingMass: totalWetMeatAndBloodWeight * (wetPercentage / 100),
          quantity: wet,
          splitPercentage: wetPercentage,
          status: 'Out of Salt',
          totalWetMeatAndBloodWeight: totalWetMeatAndBloodWeight * (wetPercentage / 100),
        };
        newItems.push(item);
      }
      if (dried > 0) {
        const driedPercentage = (dried / total) * 100;
        const item = {
          stockType: 'Dried',
          sizeMin: this.stockItem.sizeMin,
          sizeMax: this.stockItem.sizeMax,
          deliveryNoteId: this.stockItem.deliveryNoteId,
          receivedMass: this.stockItem.receivedMass * (driedPercentage / 100),
          packedMass: this.stockItem.packedMass * (driedPercentage / 100),
          harvestedMass: this.stockItem.harvestedMass * (driedPercentage / 100),
          processingMass: totalWetMeatAndBloodWeight * (driedPercentage / 100),
          quantity: dried,
          splitPercentage: driedPercentage,
          totalWetMeatAndBloodWeight: totalWetMeatAndBloodWeight * (driedPercentage / 100),
        };
        newItems.push(item);
      }
      if (newItems.length > 0) {
        const data = {
          stockItem: {
            id: this.stockItem.id,
          },
          stockItems: newItems,
        };
        await this.SPLIT_stockItems(data);
        console.debug(data);
      }
      this.isSaving = false;
      if (!skipRedirect) {
        this.$router.push({ name: 'processing-ListFactoryItem' });
      }
    },
  },
};
</script>
